/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useCallback } from 'react'
import {
  Grid,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material'
import { useLang } from '../../../../hooks/useLang'
import { authFetch } from '~/services/fetch'
import useDispatchRelatedState from '~/features/Dispatch/stores/DispatchRelatedState'
import { useHookstate } from '@hookstate/core'
import useDispatchState from '../../stores/DispatchState'
import DispatchRelatedList from './components/dispatchRelatedList'

interface dispatchDestinationProps {
  readonly id: number
  readonly code: string
}

interface DispatchTagListProps {
  readonly dispatchViewMode?: boolean
}

interface IUnifiedChildren {
  readonly id: number,
  readonly dispatch_origin_id: number,
  readonly dispatch_destination_id: number,
  readonly dispatch_origin: {
    readonly id: number,
    readonly code: string
  }
}

export default function DispatchTagList({ dispatchViewMode = false }: DispatchTagListProps) {
  const { translate } = useLang()
  const [open, setOpen] = React.useState(false)
  const [code, setCode] = React.useState('')
  const [agency, setAgency] = React.useState('')
  const [dispatchGroup, setDispatchGroup] = React.useState('')
  const [type, setType] = React.useState('')
  const [subtype, setSubtype] = React.useState('')
  const [status, setStatus] = React.useState('')
  const [unifiedChildren, setUnifiedChildren] = React.useState<readonly IUnifiedChildren[]>([])
  const [syncSuccess, setSyncSuccess] = React.useState(true)
  const [syncError, setSyncError] = React.useState<string | null>(null)

  const {
    dispatchRelated,
    syncRelatedDispatches,
    setDefaultdispatchRelated
  } = useDispatchRelatedState()
  const loading = useHookstate(true)
  const { syncDispatchFromId, dispatch, dispatchHistoric } = useDispatchState();
  const dispatchData = dispatchViewMode ? dispatchHistoric() : dispatch()
  const dispatchDestination = useHookstate<dispatchDestinationProps | null>(
    null
  )

  const handleClickOpen = (code) => {
    authFetch({
      url: `/dispatch/get-by-code`,
      method: 'POST',
      data: {
        code: code,
      },
    }).then((response) => {
      dispatchDestination.set(
        response?.data?.unified?.dispatch_destination
          ? response?.data?.unified?.dispatch_destination
          : null
      )
      setCode(code)
      setAgency(` - ${response.data.agency?.name}`)
      setDispatchGroup(`${response.data.dispatch_group?.name}`)
      setType(`${response.data.type?.name}`)
      setSubtype(
        response.data.subtype ? `- ${response.data.subtype?.name}` : ''
      )
      setStatus(`[${response.data.status?.acronym}]`)
      setUnifiedChildren(response?.data?.unified_children)
      setOpen(true)
    })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const getRelatedDispatches = async () => {
    setDefaultdispatchRelated()
    setSyncSuccess(true)
    setSyncError(null)

    try {
      const success = await syncRelatedDispatches(dispatchData.id.get())

      if (!success) {
        setSyncSuccess(false)
        setSyncError('Erro ao sincronizar despachos relacionados.')
      } else {
        if (
          dispatchData.id.get() &&
          !Object.keys(dispatchRelated().get()).includes(
            String(dispatchData.id.get())
          )
        ) {
          setSyncSuccess(false)
          setSyncError('Despacho atual não encontrado nos relacionados.')
        }
      }
    } catch (error) {
      console.error('Erro ao buscar despachos relacionados:', error)
      setSyncSuccess(false)
      setSyncError(error instanceof Error ? error.message : 'Erro desconhecido')
    }
  }


  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      loading.set(true)
      await getRelatedDispatches()

      if (isMounted) {
        loading.set(false)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [dispatchData.id.get()])

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        minHeight: 225,
        maxHeight: 225,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 4px',
        mt: 1,
      }}
    >
      <Grid
        container
        width={'100%'}
        direction={'column'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={1}
        sx={{ padding: 0 }}
      >
        {loading.get() ? (
          <CircularProgress disableShrink />
        ) : (
          <DispatchRelatedList
            handleClickOpen={handleClickOpen}
            successSync={syncSuccess}
            error={syncError}
            onRetry={getRelatedDispatches}
          />
        )}

        <Dialog fullWidth={true} open={open} onClose={handleClose}>
          <DialogTitle>
            {translate('Dispatch')} -{code} -{status}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {dispatchGroup} {agency}
            </Typography>
            <br />
            <Typography>
              {type} {subtype}
            </Typography>
            {dispatchDestination.get() ? (
              <>
                <br />
                <Typography>
                  <Stack direction={'row'} gap={1} alignItems={'center'} justifyContent={'flex-start'}>
                    {`${translate('Unified Dispatch')}: `}
                    <Chip
                      label={dispatchDestination.get()?.code}
                      onClick={() => {
                        syncDispatchFromId(dispatchDestination.get()?.id),
                          setOpen(false)
                      }}
                    />
                  </Stack>
                </Typography>
              </>
            ) : (
              false
            )}
            {unifiedChildren.length > 0 ? (
              <>
                <br />
                <Typography>
                  <Stack direction={'row'} gap={1} alignItems={'center'} justifyContent={'flex-start'} flexWrap={'wrap'}>
                    {`${translate('Absorbed Dispatches')}: `}
                    {unifiedChildren.map((children) => (
                      <Chip
                        key={children.dispatch_origin_id}
                        label={children.dispatch_origin.code}
                        onClick={() => {
                          syncDispatchFromId(children.dispatch_origin_id),
                            setOpen(false)
                        }}
                      />

                    ))}
                  </Stack>
                </Typography>
              </>
            ) : (
              false
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleClose}>
              {translate('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Paper>
  )
}
